/* -----------------Helpers & Hooks--------------- */
import { getIsOlxExam, getMappedExams } from "@helpers/exams";
import { titleCase } from "@memorang/helpers/common";
import { calculatePointsPercentage } from "@memorang/helpers/language-learning";
import { getReportSkills } from "@memorang/helpers/report";

/* -----------------Types--------------- */
import type {
	ExamStatus,
	ExamSubType,
	LegacyReport,
	MixedItemDist,
	PracticeTestExam,
} from "@memorang/types";
import type { CompletionRingProps } from "@memorang/ui/components/CompletionRing";
import type { TaskChip } from "@memorang/ui/components/HomeApplet/types";
import type { PremiumChipProps } from "@memorang/ui/components/PremiumChip";

import i18n from "@helpers/lib/i18n";
import { useAppStore } from "@hooks/useAppStore";
import { formatDate } from "@memorang/helpers/date";
import pluralize from "pluralize";

export type PracticeTestsViewSection = {
	title: string;
	data: Array<{
		list: {
			collectionId: number;
			title: string;
			description: string;
			chips: TaskChip[];
			completionRing?: CompletionRingProps;
			status: ExamStatus;
			numericId: number;
			cta: string;
			id: string;
			score: number;
			numPointsEarned?: number;
			numPointsTotal?: number;
			hasExamAccess?: boolean;
			beginStartDate?: string;
			endStartDate?: string;
			subType: ExamSubType;
			legacyReport?: LegacyReport;
			migratedSessionReport?: LegacyReport;
			scoreText?: string;
		}[][];
	}>;
	rightChip?: {
		variant: PremiumChipProps["variant"];
		tooltip?: string;
	};
	type: "practice-tests" | "quiz";
};

const getStats = (
	numQuiz: number,
	numPracticeTests: number,
	examDate?: string,
	diagnosticLabel?: Record<string, string>,
) => {
	const practiceTestLabel = useAppStore.getState().exam.practiceTest?.label;
	const stats = [
		{
			icon: "clipboard-check-multiple-outline" as const,
			label: pluralize(
				practiceTestLabel?.toLocaleLowerCase() ?? "practice tests",
				numPracticeTests,
			),
			value: numPracticeTests,
		},
		...(numQuiz
			? [
					{
						label: `${
							diagnosticLabel
								? diagnosticLabel[i18n.language]
								: "diagnostic assessment"
						}`,
						value: numQuiz,
						icon: "format-list-checks" as const,
					},
				]
			: []),
		// {
		// 	icon: "text-box-multiple-outline" as const,
		// 	label: "items",
		// 	value: numTotalItems,
		// },
		...(examDate
			? [
					{
						label: formatDate(examDate),
						icon: "calendar-outline" as const,
						value: "Exam Date:",
					},
				]
			: []),
	];
	return stats;
};

const TOPIC_FILTER_CONTEXTS = ["DASHBOARD", "REPORTS"];

const getHighlightedTopicScore = ({
	tagDetails,
	topics,
	topicsFilterContext,
}: PracticeTestExam) => {
	const isHighlightedSurface = topicsFilterContext?.some((context) =>
		TOPIC_FILTER_CONTEXTS.includes(context),
	);

	if (!(isHighlightedSurface && topics?.length && tagDetails?.length)) {
		return null;
	}

	const highlightedSkills = getReportSkills(
		tagDetails,
		topics.map((topic) => topic.toLowerCase()),
	).flatMap(({ numPointsEarned, numTotalPoints }) =>
		numPointsEarned == null || !numTotalPoints
			? []
			: [
					{
						numPointsEarned,
						numTotalPoints,
						progress: numPointsEarned / numTotalPoints,
					},
				],
	);

	if (highlightedSkills.length === 0) {
		return null;
	}

	const weakestSkill = highlightedSkills.reduce((weakest, skill) =>
		skill.progress < weakest.progress ? skill : weakest,
	);

	return {
		score: calculatePointsPercentage(
			weakestSkill.numPointsEarned,
			weakestSkill.numTotalPoints,
		),
		scoreText: `${Math.round(weakestSkill.numPointsEarned)}`,
	};
};

type MappedExamWithTopicScore = ReturnType<typeof getMappedExams>[number] & {
	scoreText?: string;
};

/**
 * Overlays the weakest highlighted topic's score onto each mapped exam,
 * leaving exams without highlighted topics untouched.
 */
const getMappedExamsWithTopicScores = (
	exams: PracticeTestExam[],
): MappedExamWithTopicScore[] => {
	const topicScoresByExamId = new Map(
		exams.flatMap((exam) => {
			const topicScore = getHighlightedTopicScore(exam);
			return topicScore ? [[exam.id, topicScore] as const] : [];
		}),
	);

	return getMappedExams(exams).map((exam) => {
		const topicScore = topicScoresByExamId.get(exam.id);
		return topicScore ? { ...exam, ...topicScore } : exam;
	});
};

export const getPracticeTestsQuizzesAndExams = (exams: PracticeTestExam[]) => {
	const practiceTests = exams
		.filter((exam) => {
			return getIsOlxExam(exam.id)
				? exam.hasAccess
				: exam.subType === "PRACTICE_TEST";
		})
		.sort((a, b) => {
			const aIsOlx = getIsOlxExam(a.id);
			const bIsOlx = getIsOlxExam(b.id);
			if (aIsOlx && !bIsOlx) {
				return -1;
			}
			if (!aIsOlx && bIsOlx) {
				return 1;
			}
			return 0;
		});
	const practiceTestQuizzes = exams
		.filter((exam) => exam.subType === "DIAGNOSTIC" && exam.numItems)
		.slice(0, 2);
	return {
		practiceTests,
		practiceTestQuizzes,
	};
};

export const getPracticeTestsData = (
	response: MixedItemDist,
	examName: string,
	examDate?: string,
	diagnosticLabel?: Record<string, string>,
) => {
	const { exams = [], hasAccess } = response;
	const { practiceTests, practiceTestQuizzes } =
		getPracticeTestsQuizzesAndExams(exams);
	const practiceTestsLabel = useAppStore.getState().exam.practiceTest?.label;

	const mappedExams = getMappedExamsWithTopicScores(practiceTests);
	const isSectionalTest = (title: string) => /section(al)?\s+test/i.test(title);
	const sectionalTests = mappedExams.filter((exam) =>
		isSectionalTest(exam.title),
	);
	const regularPracticeTests = mappedExams.filter(
		(exam) => !isSectionalTest(exam.title),
	);
	const stats = getStats(
		practiceTestQuizzes?.length,
		mappedExams?.length,
		examDate,
		diagnosticLabel,
	);
	const mappedQuizzes = getMappedExamsWithTopicScores(practiceTestQuizzes);

	const premiumRightChip = hasAccess
		? {}
		: {
				rightChip: {
					variant: "premium" as const,
					tooltip: i18n.t("dashboard.practiceTests.premiumChipTooltip", {
						examName,
					}),
				},
			};

	const practiceTestsSection: PracticeTestsViewSection | null =
		regularPracticeTests.length > 0
			? {
					title: practiceTestsLabel || i18n.t("dashboard.practiceTests.title"),
					data: [{ list: [regularPracticeTests] }],
					...premiumRightChip,
					type: "practice-tests",
				}
			: null;

	const sectionalTestsSection: PracticeTestsViewSection | null =
		sectionalTests.length > 0
			? {
					title: i18n.t("dashboard.practiceTests.sectionalTestsTitle"),
					data: [{ list: [sectionalTests] }],
					...premiumRightChip,
					type: "practice-tests",
				}
			: null;

	const freeQuizzesSection: PracticeTestsViewSection | null =
		mappedQuizzes.length > 0
			? {
					title: diagnosticLabel?.[i18n.language]
						? titleCase(diagnosticLabel[i18n.language])
						: i18n.t("dashboard.practiceTests.diagnosticAssessmentsTitle"),
					data: [
						{
							list: [mappedQuizzes],
						},
					],
					rightChip: {
						variant: "free" as const,
						tooltip: diagnosticLabel?.[i18n.language]
							? i18n.t("dashboard.practiceTests.freeChipTooltip", {
									label: titleCase(diagnosticLabel[i18n.language]),
									examName,
								})
							: i18n.t("dashboard.practiceTests.freeChipTooltipDefault", {
									examName,
								}),
					},
					type: "quiz",
				}
			: null;

	const sectionsWithRecommendedItems: PracticeTestsViewSection[] = [
		...(freeQuizzesSection ? [freeQuizzesSection] : []),
		...(practiceTestsSection ? [practiceTestsSection] : []),
		...(sectionalTestsSection ? [sectionalTestsSection] : []),
	];
	return {
		stats,
		hasAccess,
		sections: sectionsWithRecommendedItems,
		numExams: exams.length,
		mappedQuizzes,
	};
};
